import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    getDoc,
    setDoc
} from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { db, firebaseConfig } from '../firebase/config';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { devLog } from '../App';
import {
    decryptText,
    deriveKeyFromCredential,
    looksLikeEncryptedUserField,
    unwrapJsonString
} from '../utils/encryptionUtils';

const app = initializeApp(firebaseConfig);
const functions = getFunctions(app, 'us-central1');

// Cache organization data to avoid repeated calls
let cachedOrgData = null;
let cacheExpiry = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache per organization ID
const orgCache = new Map();

/**
 * Get the current authenticated user
 */
const getCurrentUser = () => {
    return new Promise((resolve, reject) => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
        }, reject);
    });
};

/**
 * Get selected organization ID from localStorage
 */
export const getSelectedOrganizationId = () => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('selectedOrganizationId');
};

/**
 * Set selected organization ID in localStorage
 */
export const setSelectedOrganization = (organizationId) => {
    if (typeof window === 'undefined') return;

    const previous = localStorage.getItem('selectedOrganizationId');
    const next = organizationId ? String(organizationId) : null;
    if (previous === next) {
        return;
    }

    if (next) {
        localStorage.setItem('selectedOrganizationId', next);
    } else {
        localStorage.removeItem('selectedOrganizationId');
    }

    // Clear all caches
    cachedOrgData = null;
    cacheExpiry = null;
    orgCache.clear();

    // Dispatch event to notify components
    window.dispatchEvent(new CustomEvent('organizationChanged', {
        detail: { organizationId: next },
    }));

    devLog(`[OrgSwitch] setSelectedOrganization: localStorage=${next}, caches cleared, event dispatched`);
};

/**
 * Get all organizations user belongs to
 */
export const getUserOrganizations = async () => {
    try {
        const getUserOrgsFunction = httpsCallable(functions, 'getUserOrganizations');
        const result = await getUserOrgsFunction();

        return result.data.organizations || [];
    } catch (error) {
        console.error('Error getting user organizations:', error);
        throw error;
    }
};

/**
 * Get user's organization data with caching
 * @param {string|null} organizationId - Optional organization ID to fetch. If not provided, uses selected or default.
 * @param {boolean} forceRefresh - Force refresh of cache
 */
export const getUserOrganizationData = async (organizationId = null, forceRefresh = false) => {
    const now = Date.now();

    // Determine which organization to fetch
    const targetOrgId = organizationId || getSelectedOrganizationId();

    // Check cache if we have a specific org ID
    if (targetOrgId && !forceRefresh) {
        const cached = orgCache.get(targetOrgId);
        if (cached && cached.expiry > now) {
            devLog('Returning cached organization data for:', targetOrgId);
            return cached.data;
        }
    }

    // Return cached data if still valid and not forcing refresh (for backward compatibility)
    if (!targetOrgId && !forceRefresh && cachedOrgData && cacheExpiry && now < cacheExpiry) {
        return cachedOrgData;
    }

    try {
        const getUserOrgFunction = httpsCallable(functions, 'getUserOrganization');
        const result = await getUserOrgFunction(targetOrgId ? { organizationId: targetOrgId } : {});

        if (!result.data.organization) {
            // Function returned null (e.g. user not in requested org or role not found). If we had a selected org, clear and retry without orgId.
            if (targetOrgId) {
                setSelectedOrganization(null);
                try {
                    const retryResult = await getUserOrgFunction({});
                    if (retryResult.data.organization) {
                        cachedOrgData = retryResult.data.organization;
                        cacheExpiry = Date.now() + CACHE_DURATION;
                        orgCache.set(retryResult.data.organization.id, { data: retryResult.data.organization, expiry: cacheExpiry });
                        return retryResult.data.organization;
                    }
                } catch (retryErr) {
                    console.error('Error getting user organization (retry):', retryErr);
                }
            }
            throw new Error('User is not part of any organization');
        }

        const orgData = result.data.organization;

        // Cache the result
        if (targetOrgId) {
            orgCache.set(targetOrgId, {
                data: orgData,
                expiry: now + CACHE_DURATION
            });
        } else {
            cachedOrgData = orgData;
            cacheExpiry = now + CACHE_DURATION;
        }

        devLog('Organization data loaded:', orgData);
        return orgData;
    } catch (error) {
        // Stale selected org: retry without orgId (wrong member, deleted org doc, or missing org id)
        const isNotMember = (error.code === 'functions/permission-denied' || error.code === 'permission-denied') && String(error.message || '').includes('not a member');
        const isOrgNotFound =
            error.code === 'functions/not-found' ||
            error.code === 'not-found' ||
            String(error.message || '').includes('Organization not found');
        if (targetOrgId && (isNotMember || isOrgNotFound)) {
            setSelectedOrganization(null);
            try {
                const getUserOrgFunction = httpsCallable(functions, 'getUserOrganization');
                const result = await getUserOrgFunction({});
                if (result.data.organization) {
                    cachedOrgData = result.data.organization;
                    cacheExpiry = Date.now() + CACHE_DURATION;
                    orgCache.set(result.data.organization.id, { data: result.data.organization, expiry: cacheExpiry });
                    return result.data.organization;
                }
                throw new Error('User is not part of any organization');
            } catch (retryErr) {
                const retryIsNoOrgUser = String(retryErr?.message || '').includes('User is not part of any organization');
                if (!retryIsNoOrgUser) {
                    console.error('Error getting user organization (retry):', retryErr);
                }
                throw retryErr;
            }
        }
        const isNoOrgUser = String(error?.message || '').includes('User is not part of any organization');
        if (!isNoOrgUser) {
            console.error('Error getting user organization:', error);
        }
        throw error;
    }
};

/**
 * Clear organization cache (call when organization data changes)
 */
export const clearOrganizationCache = () => {
    cachedOrgData = null;
    cacheExpiry = null;
    orgCache.clear();
};

/**
 * Enhanced permission checking
 */
export const checkUserPermissions = async () => {
    try {
        const orgData = await getUserOrganizationData();

        return {
            canInviteUsers: orgData?.canManageMembers || false,
            canRemoveUsers: orgData?.canManageMembers || false,
            canUpdateRoles: orgData?.canManageMembers || false,
            canEditSettings: orgData?.canEditSettings || false,
            isAdmin: orgData?.isAdmin || false,
            isEditor: orgData?.isEditor || false,
            organizationName: orgData?.name || 'Unknown',
            userRole: orgData?.role || 'member'
        };
    } catch (error) {
        console.error('Error checking permissions:', error);
        return {
            canInviteUsers: false,
            canRemoveUsers: false,
            canUpdateRoles: false,
            canEditSettings: false,
            isAdmin: false,
            isEditor: false,
            organizationName: 'Unknown',
            userRole: 'member'
        };
    }
};

/**
 * Fallback getWorkspaces function (keeping for compatibility)
 */
export const getWorkspaces = async () => {
    try {
        const getWorkspacesFunction = httpsCallable(functions, 'getWorkspaces');
        const result = await getWorkspacesFunction();
        devLog('Workspaces result:', result.data);
        return result.data;
    } catch (error) {
        console.error('Error fetching workspaces:', error);
        // Return mock data for development/testing
        return [
            {
                id: 'LprYEcApyxcXWRjAZ9UO',
                name: 'test',
                organizationId: 'demo-org',
                createdAt: 'May 20, 2025 at 9:59:18 PM UTC-4',
                documentsCount: 0,
                storageUsed: 0,
                visibility: 'organization'
            },
            {
                id: 'workspace-2',
                name: 'Marketing Team',
                organizationId: 'demo-org',
                createdAt: 'May 18, 2025 at 2:30:15 PM UTC-4',
                documentsCount: 15,
                storageUsed: 2048,
                visibility: 'organization'
            },
            {
                id: 'workspace-3',
                name: 'Product Development',
                organizationId: 'demo-org',
                createdAt: 'May 15, 2025 at 10:15:30 AM UTC-4',
                documentsCount: 42,
                storageUsed: 8192,
                visibility: 'organization'
            }
        ];
    }
};

/**
 * Updated loadWorkspacesWithOrgNames using both getUserOrganization and getWorkspaces
 */
export const loadWorkspacesWithOrgNames = async () => {
    try {
        const orgData = await getUserOrganizationData();

        if (!orgData) {
            devLog('No organization found for user');
            return [];
        }

        devLog(`User is a ${orgData.role} in organization ${orgData.name}`);

        // Use the existing getWorkspaces Cloud Function to get workspaces with proper permissions
        const getWorkspacesFunction = httpsCallable(functions, 'getWorkspaces');
        const workspacesResult = await getWorkspacesFunction();
        const allWorkspaces = workspacesResult.data || [];

        // Filter to only include workspaces from the current organization
        const orgWorkspaces = allWorkspaces.filter(workspace =>
            workspace.organizationId === orgData.id
        );

        devLog(`Found ${orgWorkspaces.length} accessible workspaces in organization`);

        // Enrich with organization data
        const workspacesWithData = orgWorkspaces.map(workspace => ({
            ...workspace,
            organizationName: orgData.name,
            orgRole: orgData.role
        }));

        return workspacesWithData;
    } catch (error) {
        console.error('Error loading workspaces:', error);
        return [];
    }
};

/**
 * Updated sendInvitations using getUserOrganization
 */
export const sendInvitations = async (emails, role, message = '', selectedWorkspaces = [], options = {}) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to send invitations');
        }

        // Get organization data
        const orgData = await getUserOrganizationData();

        if (!orgData) {
            throw new Error('No organization found for user');
        }

        // Check permissions
        if (!orgData.canManageMembers) {
            throw new Error('You do not have permission to send invitations');
        }

        // Get sender name and decrypt if encrypted
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};

        const fallbackName =
            (currentUser.displayName && String(currentUser.displayName).trim()) ||
            currentUser.email;

        let senderName = fallbackName;

        if (userData?.name && typeof userData.name === 'string') {
            if (looksLikeEncryptedUserField(userData.name)) {
                try {
                    const key = await deriveKeyFromCredential(currentUser.uid);
                    const decryptedName = await decryptText(userData.name, key);
                    if (decryptedName) {
                        const plain = unwrapJsonString(decryptedName);
                        if (typeof plain === 'string' && plain.trim()) {
                            senderName = plain.trim();
                            devLog('✅ Decrypted sender name:', senderName);
                        } else {
                            devLog('⚠️ Decryption returned empty after unwrap, using display name or email');
                        }
                    } else {
                        devLog('⚠️ Decryption returned empty, using display name or email');
                    }
                } catch (decryptError) {
                    console.error('❌ Error decrypting sender name:', decryptError);
                    devLog('⚠️ Failed to decrypt name, using display name or email');
                }
            } else if (userData.name.trim()) {
                senderName = userData.name.trim();
            }
        }

        const inviteOptions = options && typeof options === 'object' ? options : {};
        const workspaceOnly = inviteOptions.workspaceOnly === true;

        // Create invitations
        const invitations = [];
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 14);

        for (const email of emails) {
            const token = uuidv4();

            const inviteRef = await addDoc(collection(db, 'pendingInvitations'), {
                email: email.trim().toLowerCase(),
                role,
                invitedBy: currentUser.uid,
                invitedByName: senderName,
                message,
                token,
                invitedAt: serverTimestamp(),
                expiresAt,
                status: 'pending',
                organizationId: orgData.id,
                selectedWorkspaces,
                inviteType: workspaceOnly ? 'workspace' : 'organization',
                workspaceOnly
            });

            // Send email
            const sendInviteEmailFn = httpsCallable(functions, 'sendInviteEmail');
            await sendInviteEmailFn({
                email: email.trim(),
                senderName,
                role,
                message,
                token,
                expiresAt: expiresAt.toISOString(),
                workspaceCount: selectedWorkspaces.length,
                organizationId: orgData.id,
                organizationName: orgData.name, // Add organization name for email template
                workspaceOnly
            });

            devLog('Creating invite for:', email);

            invitations.push({
                id: inviteRef.id,
                email: email.trim(),
                role,
                invitedBy: senderName,
                invitedAt: new Date().toISOString().split('T')[0],
                status: 'pending'
            });
        }

        return invitations;
    } catch (error) {
        console.error('Error sending invitations:', error);
        throw error;
    }
};

/**
 * Updated getPendingInvitations using Cloud Function
 * @param {string|null} organizationId - Org to list invites for; defaults to selected org from localStorage
 */
export const getPendingInvitations = async (organizationId = null) => {
    try {
        const getPendingInvitationsFunction = httpsCallable(functions, 'getPendingInvitations');
        const orgId = organizationId ?? getSelectedOrganizationId();
        const result = await getPendingInvitationsFunction(
            orgId ? { organizationId: orgId } : {}
        );

        return result.data || [];
    } catch (error) {
        console.error('Error getting pending invitations:', error);
        throw error;
    }
};

/**
 * Cancel an invitation
 */
export const cancelInvitation = async (inviteId) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to cancel invitations');
        }

        // Check permissions
        const permissions = await checkUserPermissions();
        if (!permissions.canInviteUsers) {
            throw new Error('You do not have permission to cancel invitations');
        }

        await deleteDoc(doc(db, 'pendingInvitations', inviteId));
        return { success: true };
    } catch (error) {
        console.error('Error canceling invitation:', error);
        throw error;
    }
};

/**
 * Updated generateInviteLink using getUserOrganization
 */
export const generateInviteLink = async (role = 'viewer', selectedWorkspaces = [], options = {}) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to create invite links');
        }

        const orgData = await getUserOrganizationData();
        if (!orgData) {
            throw new Error('No organization found for user');
        }

        // Check permissions
        if (!orgData.canManageMembers) {
            throw new Error('You do not have permission to create invite links');
        }

        const inviteOptions = options && typeof options === 'object' ? options : {};
        const workspaceOnly = inviteOptions.workspaceOnly === true;
        const token = uuidv4();
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7);

        await setDoc(doc(db, 'inviteLinks', token), {
            role,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp(),
            expiresAt,
            organizationId: orgData.id,
            selectedWorkspaces,
            inviteType: workspaceOnly ? 'workspace' : 'organization',
            workspaceOnly,
            active: true
        });

        const inviteUrl = `${window.location.origin}/accept-invite?token=${token}`;

        return {
            url: inviteUrl,
            expiresAt: expiresAt.toISOString(),
            role
        };
    } catch (error) {
        console.error('Error generating invite link:', error);
        throw error;
    }
};

/**
 * Updated getTeamMembers using Cloud Function
 * @param {string|null} organizationId - Org to list members for; defaults to selected org from localStorage
 */
export const getTeamMembers = async (organizationId = null) => {
    try {
        const getTeamMembersFunction = httpsCallable(functions, 'getTeamMembers');
        const orgId = organizationId ?? getSelectedOrganizationId();
        const result = await getTeamMembersFunction(
            orgId ? { organizationId: orgId } : {}
        );

        return result.data || [];
    } catch (error) {
        console.error('Error getting team members:', error);
        throw error;
    }
};

/**
 * Updated updateUserRole using getUserOrganization
 */
export const updateUserRole = async (userId, newRole) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to update user roles');
        }

        const orgData = await getUserOrganizationData();
        if (!orgData) {
            throw new Error('No organization found for user');
        }

        // Check permissions
        if (!orgData.canManageMembers) {
            throw new Error('You do not have permission to update user roles');
        }

        // Prevent admin from changing their own role
        if (userId === currentUser.uid) {
            throw new Error('You cannot change your own role');
        }

        // Update the user's role in the organization
        const orgRef = doc(db, 'organizations', orgData.id);
        await updateDoc(orgRef, {
            [`memberRoles.${userId}`]: newRole,
            updatedAt: serverTimestamp()
        });

        // Also update the role in the user's document
        const targetUserRef = doc(db, 'users', userId);
        await updateDoc(targetUserRef, {
            role: newRole,
            updatedAt: serverTimestamp()
        });

        // Clear cache since organization data changed
        clearOrganizationCache();

        devLog(`Updated user ${userId} role to ${newRole} in organization ${orgData.id}`);
        return { success: true };
    } catch (error) {
        console.error('Error updating user role:', error);
        throw error;
    }
};

/**
 * Updated removeUser using getUserOrganization
 */
export const removeUser = async (userId) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to remove users');
        }

        // Prevent removing yourself
        if (userId === currentUser.uid) {
            throw new Error('You cannot remove yourself from the organization');
        }

        // Get organization ID - use selected org or get from backend
        let targetOrgId = getSelectedOrganizationId();

        // If no selected org, try to get it from backend (this will use the user's default org)
        if (!targetOrgId) {
            try {
                const orgData = await getUserOrganizationData(null, true);
                if (orgData && orgData.id) {
                    targetOrgId = orgData.id;
                }
            } catch (orgError) {
                console.warn('Could not get organization from backend:', orgError);
            }
        }

        if (!targetOrgId) {
            throw new Error('Unable to determine which organization to remove the user from. Please select an organization.');
        }

        // Use backend Cloud Function to remove the user (bypasses Firestore rules)
        // The backend function will verify admin permissions and find the user
        const removeUserFunction = httpsCallable(functions, 'removeUserFromOrganization');
        const result = await removeUserFunction({ userId, organizationId: targetOrgId });

        // Clear cache since organization data changed
        clearOrganizationCache();

        devLog(`Successfully removed user ${userId} from organization ${targetOrgId}`);
        return result.data || { success: true };
    } catch (error) {
        console.error('Error removing user:', error);

        // Provide more user-friendly error messages
        if (error.message && error.message.includes('not a member')) {
            throw new Error('Unable to remove user. They may have already been removed or are not a member of this organization.');
        }

        if (error.message && (error.message.includes('permission-denied') || error.message.includes('permission') || error.message.includes('insufficient'))) {
            throw new Error('You do not have permission to perform this action. Only organization admins can remove users.');
        }

        if (error.message && error.message.includes('not-found')) {
            throw new Error('User or organization not found.');
        }

        // Re-throw the original error if it's from the Cloud Function
        if (error.code && error.code.startsWith('functions/')) {
            throw error;
        }

        throw error;
    }
};

/**
 * Get all members who have access to a specific workspace
 */
export const getWorkspaceMembers = async (workspaceId) => {
    try {
        const getWorkspaceMembersFunction = httpsCallable(functions, 'getWorkspaceMembers');
        const result = await getWorkspaceMembersFunction({ workspaceId });

        return result.data.members || [];
    } catch (error) {
        console.error('Error getting workspace members:', error);
        throw error;
    }
};

/**
 * Remove a user from a specific workspace
 */
export const removeUserFromWorkspace = async (workspaceId, userId) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to remove users from workspaces');
        }

        // Get workspace to verify permissions
        const workspaceRef = doc(db, 'workspaces', workspaceId);
        const workspaceSnap = await getDoc(workspaceRef);

        if (!workspaceSnap.exists()) {
            throw new Error('Workspace not found');
        }

        const workspaceData = workspaceSnap.data();
        const userRole = workspaceData.memberRoles?.[currentUser.uid];

        // Check if user is admin of the workspace or organization
        if (userRole !== 'admin') {
            // Check organization admin status
            const orgData = await getUserOrganizationData(workspaceData.organizationId);
            if (!orgData?.canManageMembers) {
                throw new Error('You do not have permission to remove users from this workspace');
            }
        }

        // Remove user from workspace memberRoles
        const updatedMemberRoles = { ...workspaceData.memberRoles };
        delete updatedMemberRoles[userId];

        await updateDoc(workspaceRef, {
            memberRoles: updatedMemberRoles,
            updatedAt: serverTimestamp()
        });

        devLog(`Removed user ${userId} from workspace ${workspaceId}`);
        return { success: true };
    } catch (error) {
        console.error('Error removing user from workspace:', error);
        throw error;
    }
};

/**
 * Update a user's role in a specific workspace
 */
export const updateUserWorkspaceRole = async (workspaceId, userId, newRole) => {
    try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('You must be logged in to update workspace roles');
        }

        // Get workspace to verify permissions
        const workspaceRef = doc(db, 'workspaces', workspaceId);
        const workspaceSnap = await getDoc(workspaceRef);

        if (!workspaceSnap.exists()) {
            throw new Error('Workspace not found');
        }

        const workspaceData = workspaceSnap.data();
        const userRole = workspaceData.memberRoles?.[currentUser.uid];

        // Check if user is admin of the workspace or organization
        if (userRole !== 'admin') {
            // Check organization admin status
            const orgData = await getUserOrganizationData(workspaceData.organizationId);
            if (!orgData?.canManageMembers) {
                throw new Error('You do not have permission to update roles in this workspace');
            }
        }

        // Update user's role in workspace
        await updateDoc(workspaceRef, {
            [`memberRoles.${userId}`]: newRole,
            updatedAt: serverTimestamp()
        });

        devLog(`Updated user ${userId} role to ${newRole} in workspace ${workspaceId}`);
        return { success: true };
    } catch (error) {
        console.error('Error updating workspace role:', error);
        throw error;
    }
};